/**
 * **记牌器里的「一张牌」图标（span icon）。**
 *
 * 数字显示重做（用户要求）：
 *
 * - **≤ 4**：不再写数字，改为画**对应数量**的小牌图标；
 * - **> 4**：一个图标 + "×N"（例：icon ×5，× 后不留空格）；
 * - **不足 1 的部分**：不是"半张"，而是把图标**从下往上裁切**成一根进度条 ——
 *   0.2 就是露出 20% 高，0.7 就是露出 70% 高（`fractionTexture` 用子帧裁，
 *   不动几何、不挂 mask）。
 *
 * ## 为什么是预渲染纹理
 *
 * 表格是「牌型 × 指标」的笛卡尔积，一张表几百个图标。每个都现画一个
 * `Graphics` 会直接把帧率打没，所以图标**按倍率烘一次**成纹理，之后全是
 * `Sprite`；小数那份只是同一张纹理的一段**子帧**（`Texture.frame`）。
 *
 * ## 极小尺寸下的可辨识度
 *
 * 图标只有 8×11 设计像素。所以造型只保留两笔：**一个亮色圆角块**
 * （"这是一张牌"）+ **一道深色斜杠**（牌背的暗示）—— 在黑底面板上，
 * 亮块负责"看得见"，斜杠负责"不是一坨白"。
 */
import { Container, Graphics, Rectangle, Sprite, Texture, type Renderer } from 'pixi.js';
import { INK, uiPad } from '../ui/style';
import { richLabel } from '../ui/richText';
import { canHoverNow } from '../input/current';
import { t } from '../../i18n';

/** 图标的设计尺寸（乘 UI 倍率） */
const ICON_W = 8;
const ICON_H = 11;
const ICON_R = 2;
/** 图标之间的间隙（设计尺寸） */
const ICON_GAP = 2;

/** 数字 → 图标组合。纯函数，测试直接钉 */
export interface SpanCountParts {
  /** 整图标个数（整数部分 ≤ 4 时 = 整数部分；> 4 时 = 0，用 times 代替） */
  readonly full: number;
  /** 整数部分 > 4 时显示 "icon ×N"（N 为整数）；否则 null */
  readonly times: number | null;
  /** 不足 1 的部分（0..1）；> 0 时画一根从下往上裁切的小图标 */
  readonly fraction: number;
  /** 就是 0：显示一个暗色 "0"，别留空单元格 */
  readonly zero: boolean;
}

/**
 * **把数量拆成「几个整图标 / ×N / 不足 1 的进度」。**
 *
 * - `0` → 一个 "0"（空单元格会让人以为是没数据）；
 * - `> 4` → 整数部分显示成 "icon ×N"，小数部分仍是进度条；
 * - `≤ 4` → 整数部分画整图标；小数部分画从下往上裁切的图标。
 *
 * 先四舍五入到一位小数（记牌器的期望值本来就留一位小数），避免
 * `4.999999` 这类浮点噪声把整数部分吃掉。
 */
export function spanCountParts(value: number): SpanCountParts {
  const rounded = Number.isFinite(value) && value > 0 ? Math.round(value * 10) / 10 : 0;
  if (rounded === 0) return { full: 0, times: null, fraction: 0, zero: true };
  let whole = Math.floor(rounded);
  let fraction = Math.round((rounded - whole) * 10) / 10;
  if (fraction >= 1) {
    whole += 1;
    fraction = 0;
  }
  if (whole > 4) return { full: 0, times: whole, fraction, zero: false };
  return { full: whole, times: null, fraction, zero: false };
}

/** 合并状态行里三种状态的配色（已打出 / 我手里 / 未知） */
export const STATE_COLORS = {
  discarded: INK.danger,
  inHand: INK.accent,
  unknown: INK.textDim,
} as const;

/** 一张牌的三种去向（加起来 = 这一型的在场总数） */
export interface StateCounts {
  readonly discarded: number;
  readonly inHand: number;
  readonly unknown: number;
}

export interface SpanIconSet {
  readonly full: Texture;
  /** 整卡大小的**白色虚线框**：衬在进度条底下，标出"满格"有多大 */
  readonly dashed: Texture;
  /** 整图标尺寸（已乘倍率） */
  readonly width: number;
  readonly height: number;
  readonly gap: number;
  /** 小数进度条的子帧纹理缓存（按 0.1 一档） */
  readonly fractions: Map<number, Texture>;
}

const cache = new WeakMap<Renderer, Map<string, SpanIconSet>>();

/** 取当前倍率下的一套图标（每种倍率只烘一次，之后复用） */
export function spanIconSet(renderer: Renderer, scale: number): SpanIconSet {
  const key = String(Math.round(scale * 100) / 100);
  let perRenderer = cache.get(renderer);
  if (perRenderer === undefined) {
    perRenderer = new Map();
    cache.set(renderer, perRenderer);
  }
  const hit = perRenderer.get(key);
  if (hit !== undefined) return hit;

  const w = Math.max(4, Math.round(ICON_W * scale));
  const h = Math.max(6, Math.round(ICON_H * scale));
  const r = Math.max(1, Math.round(ICON_R * scale));
  // ⚠️ 用**贴图实际尺寸**，不是请求的 w/h —— `renderer.generateTexture` 会
  //    加一点点 padding，按请求尺寸排版会让折行的每一行叠在一起（"行高没涨"）。
  const full = bake(renderer, (g) => drawCard(g, w, h, r, scale));
  const dashed = bake(renderer, (g) => drawDashedCard(g, w, h, scale));
  const set: SpanIconSet = {
    full,
    dashed,
    width: full.width,
    height: full.height,
    gap: uiPad(ICON_GAP, scale),
    fractions: new Map(),
  };
  perRenderer.set(key, set);
  return set;
}

function drawCard(g: Graphics, w: number, h: number, r: number, scale: number): void {
  g.roundRect(0, 0, w, h, r).fill({ color: INK.text, alpha: 0.92 });
  // 一道深色斜杠：牌背的暗示。线宽随倍率走，但至少 1px，否则极小尺寸下会消失
  g.moveTo(w * 0.24, h * 0.78)
    .lineTo(w * 0.76, h * 0.22)
    .stroke({ width: Math.max(1, uiPad(1.2, scale)), color: INK.panel });
}

/** 整卡大小的虚线框（角度不做圆角，极小尺寸下更清楚） */
function drawDashedCard(g: Graphics, w: number, h: number, scale: number): void {
  const stroke = Math.max(1, uiPad(1, scale));
  // ★ 内缩半个线宽：不内缩的话 stroke 会撑出 (0,0,w,h) 几个像素，
  //   烘出来的纹理就比整卡图标大一圈（用户报过）。
  const pad = stroke / 2;
  const x0 = pad;
  const y0 = pad;
  const x1 = w - pad;
  const y1 = h - pad;
  const on = Math.max(1.5, uiPad(1.7, scale));
  const off = Math.max(1, uiPad(1.3, scale));
  const step = on + off;
  for (let x = x0; x < x1; x += step) {
    const xe = Math.min(x1, x + on);
    g.moveTo(x, y0).lineTo(xe, y0);
    g.moveTo(x, y1).lineTo(xe, y1);
  }
  for (let y = y0; y < y1; y += step) {
    const ye = Math.min(y1, y + on);
    g.moveTo(x0, y).lineTo(x0, ye);
    g.moveTo(x1, y).lineTo(x1, ye);
  }
  g.stroke({ width: stroke, color: INK.text, alpha: 0.85 });
}

function bake(renderer: Renderer, draw: (g: Graphics) => void): Texture {
  const g = new Graphics();
  draw(g);
  const texture = renderer.generateTexture(g);
  g.destroy();
  return texture;
}

/**
 * **小数进度条**：整图标的**下 1/f 段**子帧。
 *
 * 用 `Texture.frame` 裁而不是 `mask` —— 子帧不增加显示对象、不进包围盒，
 * 于是单元格宽度量得准；mask 会让一个透明 Graphics 参与布局。
 */
export function fractionTexture(set: SpanIconSet, fraction: number): Texture {
  const key = Math.max(1, Math.min(10, Math.round(fraction * 10))) / 10;
  const hit = set.fractions.get(key);
  if (hit !== undefined) return hit;
  const h = Math.max(1, Math.round(set.height * key));
  const frame = set.full.frame;
  const texture = new Texture({
    source: set.full.source,
    frame: new Rectangle(frame.x, frame.y + frame.height - h, frame.width, h),
  });
  set.fractions.set(key, texture);
  return texture;
}

/**
 * **合并状态行的一格**：按状态上色的整卡图标，超过 `wrapWidth` 就**折行**。
 *
 * 这一格**不用 "×N"** —— 颜色本身就是状态，张数必须一张张画出来才读得懂。
 */
export function buildStateIcons(
  renderer: Renderer,
  counts: StateCounts,
  scale: number,
  wrapWidth: number,
): Container {
  const set = spanIconSet(renderer, scale);
  const row = new Container();
  row.label = 'state-icons';
  let x = 0;
  let y = 0;
  const add = (color: number, n: number): void => {
    const total = Math.max(0, Math.round(n));
    for (let i = 0; i < total; i++) {
      if (x > 0 && x + set.width > wrapWidth) {
        x = 0;
        y += set.height + set.gap;
      }
      const sprite = new Sprite(set.full);
      sprite.tint = color;
      sprite.position.set(x, y);
      row.addChild(sprite);
      x += set.width + set.gap;
    }
  };
  add(STATE_COLORS.discarded, counts.discarded);
  add(STATE_COLORS.inHand, counts.inHand);
  add(STATE_COLORS.unknown, counts.unknown);
  return row;
}

/**
 * **状态图例**：三个色块图标 + 文字（已打出 / 我手里 / 未知）。
 *
 * `hint` 给一句「怎么查看详情」的提示（悬停 / 点按），由调用方按当前输入画像挑；
 * 不给就不画（走焦时不提示）。它画在图标那一行**下面**，行高由容器自己量。
 */
export function buildStateLegend(renderer: Renderer, scale: number, hint: string | null = null): Container {
  const set = spanIconSet(renderer, scale);
  const row = new Container();
  row.label = 'state-legend';
  const items: readonly (readonly [number, string])[] = [
    [STATE_COLORS.discarded, t('render.legend.discarded')],
    [STATE_COLORS.inHand, t('render.legend.inHand')],
    [STATE_COLORS.unknown, t('render.legend.unknown')],
  ];
  let x = 0;
  for (const [color, text] of items) {
    const icon = new Sprite(set.full);
    icon.tint = color;
    icon.position.set(x, 0);
    row.addChild(icon);
    const lab = richLabel({ text, scale, size: 11, fill: INK.textDim, role: 'cjk' });
    lab.position.set(x + set.width + uiPad(4, scale), (set.height - lab.height) / 2);
    row.addChild(lab);
    x += set.width + uiPad(4, scale) + lab.width + uiPad(14, scale);
  }
  if (hint !== null) {
    const note = richLabel({ text: hint, scale, size: 11, fill: INK.textDim, role: 'cjk' });
    note.position.set(0, set.height + uiPad(4, scale));
    row.addChild(note);
  }
  return row;
}

/**
 * **数字格的 hover / 点按回调。**
 *
 * 数字格用图标表示，读不出精确的小数；指上去（触控：点一下）用说明面板讲清楚。
 * 组件只负责**什么时候**叫回调，不讲什么内容 —— 那是调用方的事。
 */
export interface SpanCellHandlers {
  /** 能 hover 的指针指进来了 */
  readonly enter: () => void;
  /** 能 hover 的指针移开了 */
  readonly leave: () => void;
  /** 触控点了一下（调用方自己切换开关） */
  readonly toggle: () => void;
}

/**
 * **给一个数字格挂上 hover / 点按。**
 *
 * ⚠️ 挂在 `src/render/game/`（组件层）而不是屏幕层：屏幕层不许自己挂指针事件
 *    （`test/ui/design.test.ts`）。`canHoverNow()` 的闸门也在这里 —— 触控点完
 *    浏览器还会把指针位置留在原地，不能算 hover。
 */
export function bindSpanCell(cell: Container, handlers: SpanCellHandlers): void {
  cell.eventMode = 'static';
  const b = cell.getLocalBounds();
  cell.hitArea = new Rectangle(b.x, b.y, Math.max(1, b.width), Math.max(1, b.height));
  cell.on('pointerover', () => {
    if (!canHoverNow()) return;
    handlers.enter();
  });
  cell.on('pointerout', () => {
    if (!canHoverNow()) return;
    handlers.leave();
  });
  cell.on('pointertap', () => {
    // 能 hover 的指针不需要点按；触控横滚拖过来的那一下由调用方用 justDragged 挡
    if (canHoverNow()) return;
    handlers.toggle();
  });
}

/** **造一个数量单元格**。返回容器的宽度即排版宽度（调用方量完再摆） */
export function buildSpanCount(renderer: Renderer, value: number, scale: number): Container {
  const set = spanIconSet(renderer, scale);
  const parts = spanCountParts(value);
  const row = new Container();
  row.label = 'span-count';

  if (parts.zero) {
    row.addChild(richLabel({ text: '0', scale, size: 12, fill: INK.textDim, role: 'mono' }));
    return row;
  }

  let x = 0;
  const addSprite = (texture: Texture, y = 0): void => {
    const sprite = new Sprite(texture);
    sprite.position.set(x, y);
    row.addChild(sprite);
    x += sprite.width + set.gap;
  };

  if (parts.times !== null) {
    addSprite(set.full);
    // "×N"（× 后不留空格）
    const text = richLabel({ text: '×' + String(parts.times), scale, size: 12, fill: INK.text, role: 'mono' });
    text.position.set(x, (set.height - text.height) / 2);
    row.addChild(text);
    x += text.width + set.gap;
  } else {
    for (let i = 0; i < parts.full; i++) addSprite(set.full);
  }

  if (parts.fraction > 0) {
    // ★ 衬一个整卡大小的白色虚线框，再把底部 fraction 段的高亮盖上去 ——
    //   这样 0.2 / 0.7 不只看得出高矮，还看得出"占满格的比例"。
    const dashed = new Sprite(set.dashed);
    dashed.position.set(x, 0);
    row.addChild(dashed);
    const texture = fractionTexture(set, parts.fraction);
    const fill = new Sprite(texture);
    // 底边和虚线框对齐 → 看起来是"从下往上长出来"的
    fill.position.set(x, set.height - texture.height);
    row.addChild(fill);
    x += set.width + set.gap;
  }
  return row;
}
